import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import { CardBody, CardHeader } from "reactstrap";
import Filters from "../../../../components/Table/Actions/Filters";
import ColumnToggle from "../../../../components/NTable/Actions/ColumnToggle";
import Pagination from "../../../../components/NTable/Pagination";
import Table from "./Table";
import Header from "./Header";
import { useTable } from "./hooks/useTable";
import { useColumns } from "./hooks/useColumns";
import { useProfileDrawer } from "./hooks/useProfileDrawer";
import { useDateFilters } from "../../hooks/useDateFilters";
import { useGroups } from "../../../Settings/General/OrganisationLevelGroups/context";
import { useMultiprofileData } from "./hooks/useMultiprofileData";
import { useFilters } from "../../filtersContext";
import { useFiltersConfig } from "./hooks/useFiltersConfig";
import { useElementHeight } from "../../../../utils/hooks/useElementHeight";
import { useInitialState } from "../../../../utils/hooks/useInitialState";
import { formatTimeSheetParams } from "../../api/helpers";
import "./style.scss";

function Overtime({ isMyOvertime = false }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const { ref: elementHeightRef, height } = useElementHeight();
    const { open: openProfileDrawer, drawer: profileDrawer } = useProfileDrawer();
    const { from, to } = useDateFilters();
    const { groups } = useGroups();
    const [filters, onFiltersChange] = useFilters();
    const filtersConfig = useFiltersConfig();
    const columns = useColumns({ viewProfile: !isMyOvertime ? openProfileDrawer : undefined });

    const params = useMemo(() => {
        if (isMyOvertime) {
            return {
                startDate: moment(from).format("YYYY-MM-DD"),
                endDate: moment(to).format("YYYY-MM-DD")
            }
        }
        return {
            page: page + 1,
            perPage,
            ...formatTimeSheetParams({
                filters,
                from,
                to
            })
        }
    }, [filters, from, isMyOvertime, page, perPage, to]);

    const { data, isLoading, totalItems } = useMultiprofileData({ isMyOvertime, params });

    const table = useTable({
        data,
        columns,
        loading: isLoading,
        canExpand: !isMyOvertime,
        totalItems,
        initialState: useInitialState(() => {
            const columnVisibility = {
                calculationGroups: false,
                jobs: false,
                locations: false
            }

            const columnFilters = [
                {
                    method: "contains",
                    name: "employeeNumber",
                    value: "",
                },
            ];
            if (!isMyOvertime) {
                return { columnVisibility, columnFilters };
            }
            return { columnVisibility };
        })
    });

    useEffect(() => {
        table.setColumnVisibility((prev) => {
            const columnVisibility = prev;
            groups.forEach((group) => {
                const value = group.level;
                columnVisibility[value] = group.allocatedBy;
            })
            return columnVisibility;
        })
    }, [groups, table]);

    const removeEmployeeNumberParam = useCallback(() => {
        setSearchParams(prevSearchParams => {
            prevSearchParams.delete("employeeNumber");
            return prevSearchParams;
        }, { replace: true })
    }, [setSearchParams]);

    useEffect(() => {
        const employeeNumber = searchParams.get("employeeNumber");
        if (employeeNumber && isMyOvertime) {
            table.toggleAllRowsExpanded(true);
            onFiltersChange([{
                name: "employeeNumber",
                method: "contains",
                value: employeeNumber
            }]);
            removeEmployeeNumberParam();
        }
    }, [onFiltersChange, removeEmployeeNumberParam, searchParams, table, isMyOvertime]);

    useEffect(() => {
        if (isMyOvertime) {
            table.toggleAllRowsExpanded(true);
        }
    }, [isMyOvertime, table]);

    return (
        <>
            <CardHeader>
                <Header />
            </CardHeader>
            <CardBody className="px-2">
                <div style={{ height: "calc(100% - 50px)" }}>
                    <div ref={elementHeightRef} className="d-flex justify-content-end mb-2">
                        {!isMyOvertime && (
                            <Filters
                                columns={filtersConfig}
                                filters={filters}
                                setFilters={onFiltersChange}
                            />
                        )}
                        <ColumnToggle columns={table.getAllColumns()} />
                    </div>
                    <div className="m-0 p-0" style={{ maxHeight: `calc(100% - ${height}px)`, overflowY: "auto" }}>
                        <Table
                            table={table}
                            params={params}
                            isMyOvertime={isMyOvertime}
                        />
                    </div>

                    {!isMyOvertime && (
                        <Pagination
                            totalItems={totalItems || 0}
                            page={page}
                            perPage={perPage}
                            gotoPage={setPage}
                            setPerPage={setPerPage}
                            pageCount={Math.ceil(totalItems / perPage)}
                        />
                    )}

                    {profileDrawer}
                </div>
            </CardBody>
        </>
    );
}

export default Overtime;
